import { useCallback, useEffect, useState } from "react";
import { redirect } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

export type SchoolMembership = {
  school_id: string;
  role: Database["public"]["Enums"]["school_role"];
  school: {
    id: string;
    name: string;
    logo_url: string | null;
    currency: string;
  };
};

const ACTIVE_KEY = "makai.activeSchool";

function readActive(): string | null {
  if (typeof window === "undefined") return null;
  try { return window.localStorage.getItem(ACTIVE_KEY); } catch { return null; }
}
function writeActive(id: string | null) {
  if (typeof window === "undefined") return;
  if (id) window.localStorage.setItem(ACTIVE_KEY, id);
  else window.localStorage.removeItem(ACTIVE_KEY);
  window.dispatchEvent(new CustomEvent("makai:school"));
}

export async function fetchMyMemberships(): Promise<SchoolMembership[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from("school_members")
    .select("school_id, role, school:schools(id, name, logo_url, currency)")
    .eq("user_id", user.id)
    .eq("status", "active");
  if (error) { console.error(error); return []; }
  return (data ?? []).filter((r) => r.school).map((r) => ({
    school_id: r.school_id,
    role: r.role,
    school: r.school as SchoolMembership["school"],
  }));
}

/**
 * Gate for /admin/* routes. Confirms the user has at least one school where
 * they hold an admin/principal role. Otherwise sends them to /admin/onboarding.
 */
export async function requireSchoolAdminOrOnboard() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw redirect({ to: "/auth" });

  const memberships = await fetchMyMemberships();
  const admin = memberships.filter((m) => m.role === "admin" || m.role === "principal");
  if (admin.length === 0) throw redirect({ to: "/admin/onboarding" });

  const active = readActive();
  if (!active || !admin.find((m) => m.school_id === active)) {
    writeActive(admin[0].school_id);
  }
}

/** Onboarding route guard: must be signed in, but no school required. */
export async function requireAuthForOnboarding() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw redirect({ to: "/auth" });
}

export function useSchoolContext() {
  const [memberships, setMemberships] = useState<SchoolMembership[]>([]);
  const [activeId, setActiveId] = useState<string | null>(() => readActive());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    const list = await fetchMyMemberships();
    setMemberships(list);
    const adminList = list.filter((m) => m.role === "admin" || m.role === "principal");
    const current = readActive();
    if (!current || !adminList.find((m) => m.school_id === current)) {
      const next = adminList[0]?.school_id ?? null;
      writeActive(next);
      setActiveId(next);
    } else {
      setActiveId(current);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
    const onChange = () => setActiveId(readActive());
    window.addEventListener("makai:school", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("makai:school", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, [refresh]);

  const switchSchool = useCallback((id: string) => { writeActive(id); setActiveId(id); }, []);

  const adminMemberships = memberships.filter((m) => m.role === "admin" || m.role === "principal");
  const active = memberships.find((m) => m.school_id === activeId) ?? adminMemberships[0] ?? null;

  return { memberships, adminMemberships, activeId: active?.school_id ?? null, active, loading, switchSchool, refresh };
}
